import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { heroSlides, projectsData } from '../data/mock';

const Homepage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const { t, language } = useLanguage();
  const sectionRefs = useRef([]);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-play hero slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Counter animation
  const [counters, setCounters] = useState({ completed: 0, residential: 0, total: 0, years: 0 });

  useEffect(() => {
    const targets = { completed: 7, residential: 10000, total: 11, years: 10 };
    const duration = 2500;
    const steps = 60;
    const interval = duration / steps;
    const increments = {
      completed: targets.completed / steps,
      residential: targets.residential / steps,
      total: targets.total / steps,
      years: targets.years / steps
    };

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      if (currentStep <= steps) {
        setCounters({
          completed: Math.min(Math.floor(increments.completed * currentStep), targets.completed),
          residential: Math.min(Math.floor(increments.residential * currentStep), targets.residential),
          total: Math.min(Math.floor(increments.total * currentStep), targets.total),
          years: Math.min(Math.floor(increments.years * currentStep), targets.years)
        });
      } else {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Decorative Curved Lines */}
      <div className="curved-lines">
        <svg className="absolute -left-1/4 top-0 h-full w-1/2" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0,0 Q50,50 0,100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.2" />
          <path d="M10,0 Q60,50 10,100" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
          <path d="M20,0 Q70,50 20,100" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.2" />
        </svg>
        <svg className="absolute -right-1/4 top-0 h-full w-1/2" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M100,0 Q50,50 100,100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.2" />
          <path d="M90,0 Q40,50 90,100" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.2" />
        </svg>
      </div>

      {/* Hero Section - Full Screen */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Slides with Parallax */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1500 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black z-10" />
            <img
              src={slide.image}
              alt={slide.title[language] || slide.title.en}
              className="w-full h-full object-cover"
              style={{ transform: `translateY(${scrollY * 0.3}px) scale(1.1)` }}
            />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-20 text-center px-6 max-w-5xl mx-auto">
          <p className="text-sm sm:text-base uppercase tracking-[0.4em] text-white/60 mb-6 font-light animate-fade-in-up">
            {language === 'en' ? 'WELCOME TO' : language === 'ar' ? 'مرحباً بكم في' : 'بەخێربێن بۆ'}
          </p>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white tracking-tight mb-6 animate-fade-in-up delay-200">
            {heroSlides[currentSlide].title[language] || heroSlides[currentSlide].title.en}
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl text-white/70 font-light mb-10 max-w-2xl mx-auto animate-fade-in-up delay-300">
            {heroSlides[currentSlide].subtitle[language] || heroSlides[currentSlide].subtitle.en}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up delay-400">
            <Link
              to="/projects"
              className="btn-luxury px-10 py-4 bg-white text-black text-xs uppercase tracking-[0.2em] font-medium hover:bg-white/90"
            >
              {t('hero.exploreCTA')}
            </Link>
            <Link
              to="/contact"
              className="btn-luxury px-10 py-4 border border-white/50 text-white text-xs uppercase tracking-[0.2em] font-light hover:bg-white hover:text-black transition-all"
            >
              {t('hero.contactCTA')}
            </Link>
          </div>
        </div>

        {/* Slide Indicators - Hidden on mobile */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 hidden sm:flex items-center gap-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all duration-500 ${
                index === currentSlide
                  ? 'w-12 h-1 bg-white'
                  : 'w-6 h-0.5 bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30">
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs uppercase tracking-widest text-white/40">
              {language === 'en' ? 'Scroll' : language === 'ar' ? 'تمرير' : 'سکرۆڵ'}
            </span>
            <ChevronDown className="w-5 h-5 text-white/40" />
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="relative py-32 sm:py-40 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6 font-light scroll-reveal">
            {language === 'en' ? 'THE GATEWAY OF KURDISTAN DEVELOPMENT' : language === 'ar' ? 'بوابة تطوير كردستان' : 'دەروازەی گەشەپێدانی کوردستان'}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-white leading-relaxed scroll-reveal">
            {t('hero.subtitle')}
          </h2>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="relative py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              { value: counters.completed, label: t('metrics.completedProjects') },
              { value: `${counters.residential.toLocaleString()}+`, label: t('metrics.residentialProjects') },
              { value: counters.total, label: t('metrics.totalConstructions') },
              { value: `${counters.years}+`, label: t('metrics.yearsOfService') }
            ].map((metric, index) => (
              <div 
                key={index} 
                className={`bg-black p-6 sm:p-10 text-center scroll-reveal-scale overflow-hidden`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-white mb-4 whitespace-nowrap">
                  {metric.value}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/50 font-light">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="relative py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light scroll-reveal">
              {language === 'en' ? 'OUR PORTFOLIO' : language === 'ar' ? 'أعمالنا' : 'پۆرتفۆلیۆکەمان'}
            </p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight scroll-reveal">
              {t('featuredProjects.title')}
            </h2>
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectsData.filter(p => p.id !== 'nawzhen_city' && p.id !== 'dream_land').map((project, index) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className={`group relative h-[550px] overflow-hidden image-zoom-container scroll-reveal-scale tilt-3d`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-10 transition-all duration-500 group-hover:via-black/60" />
                <img
                  src={project.images[0]}
                  alt={project.name[language] || project.name.en}
                  className="w-full h-full object-cover image-zoom"
                />
                
                <div className="absolute inset-0 z-20 flex flex-col justify-end p-8">
                  {/* Project Logo */}
                  <div className="flex justify-center mb-6">
                    <img
                      src={project.logo}
                      alt={project.name[language] || project.name.en}
                      className={`object-contain transition-transform duration-500 group-hover:scale-110 drop-shadow-[0_0_15px_rgba(255,255,255,0.25)] ${
                        (project.id === 'nawzhen_city' || project.id === 'dream_land')
                          ? 'h-20 w-20'
                          : 'h-24 max-w-[220px]'
                      }`}
                    />
                  </div>
                  
                  {/* Location */}
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-light mb-4">
                    {project.location}
                  </p>
                  
                  {/* Status Badge */}
                  <div className="mb-6">
                    <span className={`inline-block px-4 py-1 text-[10px] uppercase tracking-widest font-light ${
                      project.status === 'completed' 
                        ? 'bg-white text-black' 
                        : 'bg-white/10 text-white border border-white/30'
                    }`}>
                      {project.status === 'completed' 
                        ? (language === 'en' ? 'Completed' : language === 'ar' ? 'مكتمل' : 'تەواوبوو')
                        : (language === 'en' ? 'Ongoing' : language === 'ar' ? 'جاري' : 'بەردەوام')
                      }
                    </span>
                  </div>

                  {/* View Details Link */}
                  <div className="flex items-center gap-3 text-white text-xs uppercase tracking-[0.15em] font-light opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                    <span className="border-b border-white/50 pb-1">{t('featuredProjects.viewDetails')}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* View All Projects */}
          <div className="text-center mt-16 scroll-reveal">
            <Link
              to="/projects"
              className="btn-luxury inline-flex items-center gap-3 px-10 py-4 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-light hover:bg-white hover:text-black transition-all"
            >
              {language === 'en' ? 'View All Projects' : language === 'ar' ? 'عرض جميع المشاريع' : 'هەموو پڕۆژەکان ببینە'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative py-32 bg-[#0a0a0a] overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Text Content */}
            <div className="scroll-reveal-left">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6 font-light">
                {language === 'en' ? 'ABOUT US' : language === 'ar' ? 'من نحن' : 'دەربارەمان'}
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-8 leading-tight">
                {language === 'en' ? 'A VISIONARY CITY CALLS FOR INNOVATIVE LIVING' :
                 language === 'ar' ? 'مدينة صاحبة رؤية تدعو إلى حياة مبتكرة' :
                 'شارێکی بینینی داوا لە ژیانی داهێنەرانە دەکات'}
              </h2>
              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed mb-10">
                {t('about.story.content')}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-3 text-white text-xs uppercase tracking-[0.2em] font-light border-b border-white/50 pb-2 hover:border-white transition-colors line-expand"
              >
                {language === 'en' ? 'Learn More About Us' : language === 'ar' ? 'اعرف المزيد عنا' : 'زیاتر بزانە دەربارەمان'}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Image */}
            <div className="scroll-reveal-right">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_37c30a3a-473e-4636-8923-f847e72da520/artifacts/o6q8s2le_scene%205%20.jpg"
                  alt="About Darwazay Garmyan"
                  className="w-full h-full object-cover"
                />
                {/* Decorative Element */}
                <div className="absolute -bottom-10 -right-10 w-40 h-40 border border-white/20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
        <img
          src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY4NjMyMDc5fDA&ixlib=rb-4.1.0&q=85"
          alt="Contact Us"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: `translateY(${(scrollY - 2000) * 0.2}px)` }}
        />
        
        <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light scroll-reveal">
            {language === 'en' ? 'GET IN TOUCH' : language === 'ar' ? 'تواصل معنا' : 'پەیوەندیمان پێوە بکە'}
          </p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-10 scroll-reveal">
            {t('cta.title')}
          </h2>
          <Link
            to="/contact"
            className="btn-luxury inline-block px-12 py-5 bg-white text-black text-xs uppercase tracking-[0.2em] font-medium hover:bg-white/90 scroll-reveal"
          >
            {t('cta.button')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
